import {
    Fragment,
    FunctionComponent,
    ReactNode,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { Stack, SvgIcon, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ArrowDownOnSquareIcon from '@heroicons/react/24/solid/ArrowDownOnSquareIcon';
import { GetTicketsSearchParams, useGetTicketsQuery } from '../api/ticket.api';
import { TicketsFilter } from './ticketsFilter';
import { TicketsOverviewUI } from './ticketsOverviewUI';
import { LiftOperator } from '../../liftoperator/model/liftoperator.types';
import { TicketTableColumnId } from '../lib/hooks/useTicketColumns';
import { TicketContentBlockId } from '../lib/hooks/useTicketDetails';

type DateRange = [Date | null, Date | null];

const isValidDate = (d: Date | null): d is Date =>
    d instanceof Date && !isNaN(d.getTime());

/**
 * Keys from search params that should be synced to URL
 */
export const ticketSyncKeys = [
    'userId',
    'orderId',
    'query',
    'orderDateFrom',
    'orderDateTo',
    'ticketStartFrom',
    'ticketStartTo',
] as const;

/**
 * Serializes ticket search params to URL search string
 */
const serializeParamsToUrl = (
    params: GetTicketsSearchParams,
    keys: readonly string[]
): string => {
    const urlParams = new URLSearchParams();
    for (const key of keys) {
        const value = params[key as keyof GetTicketsSearchParams];
        if (value !== undefined && value !== null && value !== '') {
            if (value instanceof Date) {
                urlParams.set(key, value.toISOString());
            } else {
                urlParams.set(key, String(value));
            }
        }
    }
    return urlParams.toString();
};

export type B2BUser = {
    id: string;
    name: string;
};

export type TicketsContainerProps = {
    /** Initial search params (e.g., from URL query params) */

    initialParams?: GetTicketsSearchParams;
    /**
     * Base path for URL sync (e.g., '/tickets').
     * When provided together with navigate, the container will automatically sync search params to URL.
     */
    basePath?: string;

    /**
     * Navigation function for URL sync (e.g., from useNavigate hook).
     * Required when basePath is provided.
     */
    navigate?: (url: string, options?: { replace?: boolean }) => void;

    /** Callback when search params change (called in addition to URL sync) */
    onSearchParamsChange?: (params: GetTicketsSearchParams) => void;
    /** Export tickets function - returns a blob */
    onExport?: (params: GetTicketsSearchParams) => Promise<Blob>;
    /** List of lift operators for display */
    liftOperators?: LiftOperator[];

    /** List of B2B users for the dropdown filter */
    b2bUsers?: B2BUser[];

    /** Current locale */
    locale?: string;

    /** Translations object */
    translations?: {
        en?: Record<string, string>;
        de?: Record<string, string>;
    };

    /** Translation function */
    t: (key: string) => string;

    /** Columns to hide in the table */
    hideColumns?: TicketTableColumnId[];

    /** Fields to hide in detail view */
    hideDetailFields?: TicketContentBlockId[];

    /**
     * Render prop for the order date range picker.
     */
    renderOrderDateRange: (props: {
        value: DateRange;
        onChange: (value: DateRange) => void;
        fromText: string;
        toText: string;
    }) => ReactNode;

    /**
     * Render prop for the ticket start date range picker.
     */
    renderTicketStartDateRange: (props: {
        value: DateRange;
        onChange: (value: DateRange) => void;
        fromText: string;
        toText: string;
    }) => ReactNode;

    /** Debounce delay for search query in ms */
    debounceDelay?: number;
};

/**
 * Custom hook for debounced effects
 */
const useDebouncedEffect = (
    effect: () => void,
    delay: number,
    deps: unknown[]
) => {
    useEffect(() => {
        const handler = setTimeout(effect, delay);
        return () => clearTimeout(handler);
    }, deps);
};

export const TicketsContainer: FunctionComponent<TicketsContainerProps> = ({
    initialParams = {},
    basePath,
    navigate,
    onSearchParamsChange,
    onExport,
    liftOperators,
    b2bUsers,
    locale = 'de',
    translations,
    t,
    hideColumns = ['liftOperator'],
    hideDetailFields = ['liftOperator'],
    renderOrderDateRange,
    renderTicketStartDateRange,
    debounceDelay = 450,
}) => {
    const [exportLoading, setExportLoading] = useState(false);
    const [query, setQuery] = useState(initialParams.query || '');
    const [insuranceChecked, setInsuranceChecked] = useState(false);
    const [ticketSearch, setTicketSearch] = useState<GetTicketsSearchParams>({
        ...initialParams,
        sort: 'orderDate,desc',
        hasInsurance: false,
    });

    const { data: ticketResult, isFetching: isLoading } =
        useGetTicketsQuery(ticketSearch);

    // Sync insurance checkbox with ticketSearch
    if (insuranceChecked !== ticketSearch.hasInsurance) {
        setTicketSearch((prevState) => ({
            ...prevState,
            hasInsurance: insuranceChecked,
        }));
    }

    // Debounced query update
    useDebouncedEffect(
        () => {
            setTicketSearch((prevState) => {
                if (query) {
                    return { ...prevState, query };
                } else {
                    const nextState = { ...prevState };
                    delete nextState.query;
                    return nextState;
                }
            });
        },
        debounceDelay,
        [query]
    );

    // Sync search params to URL and notify parent
    useEffect(() => {
        // URL sync when basePath and navigate are provided
        if (basePath && navigate) {
            const searchString = serializeParamsToUrl(
                ticketSearch,
                ticketSyncKeys
            );
            const url = searchString ? `${basePath}?${searchString}` : basePath;
            navigate(url, { replace: true });
        }

        // Also call the optional callback
        onSearchParamsChange?.(ticketSearch);
    }, [ticketSearch, basePath, navigate, onSearchParamsChange]);

    const handleExportClick = useCallback(async () => {
        if (!onExport) return;

        setExportLoading(true);
        try {
            await onExport(ticketSearch);
        } finally {
            setExportLoading(false);
        }
    }, [ticketSearch, onExport]);

    const handleReset = useCallback(() => {
        setQuery('');
        setInsuranceChecked(false);
        setTicketSearch({ sort: 'orderDate,desc' });
    }, []);

    const updateOrderDateRange = useCallback((value: DateRange) => {
        const [from, to] = value;
        setTicketSearch((prevState) => ({
            ...prevState,
            ...(isValidDate(from) || from === null
                ? { orderDateFrom: from ?? undefined }
                : {}),
            ...(isValidDate(to) || to === null
                ? { orderDateTo: to ?? undefined }
                : {}),
        }));
    }, []);

    const updateTicketStartDateRange = useCallback((value: DateRange) => {
        const [from, to] = value;
        setTicketSearch((prevState) => ({
            ...prevState,
            ...(isValidDate(from) || from === null
                ? { ticketStartFrom: from ?? undefined }
                : {}),
            ...(isValidDate(to) || to === null
                ? { ticketStartTo: to ?? undefined }
                : {}),
        }));
    }, []);

    const updateB2BUser = useCallback((userId: string | undefined) => {
        setTicketSearch((prevState) => ({
            ...prevState,
            userId: userId === '-' ? undefined : userId,
        }));
    }, []);

    return (
        <Fragment>
            <Typography sx={{ mb: 8 }} variant='h4'>
                Tickets
            </Typography>

            <Stack alignItems='flex-start' spacing={4}>
                {onExport && (
                    <LoadingButton
                        loading={exportLoading}
                        onClick={handleExportClick}
                        color='inherit'
                        startIcon={
                            <SvgIcon fontSize='small'>
                                <ArrowDownOnSquareIcon />
                            </SvgIcon>
                        }
                    >
                        {t('ticketsPage.export')}
                    </LoadingButton>
                )}

                <TicketsFilter
                    query={query}
                    onQueryChange={setQuery}
                    ticketSearch={ticketSearch}
                    insuranceChecked={insuranceChecked}
                    onInsuranceChange={setInsuranceChecked}
                    onOrderDateRangeChange={updateOrderDateRange}
                    onTicketStartDateRangeChange={updateTicketStartDateRange}
                    onReset={handleReset}
                    t={t}
                    renderOrderDateRange={renderOrderDateRange}
                    renderTicketStartDateRange={renderTicketStartDateRange}
                    b2bUsers={b2bUsers}
                    onB2BUserChange={updateB2BUser}
                />

                <TicketsOverviewUI
                    currentPageTickets={ticketResult?.content}
                    ticketsTotal={ticketResult?.totalElements}
                    ticketsPerPage={ticketSearch.size || 25}
                    onPageSizeChange={(nextValue) => {
                        setTicketSearch((prevState) => ({
                            ...prevState,
                            size: nextValue,
                        }));
                    }}
                    onPageChange={(nextValue) => {
                        setTicketSearch((prevState) => ({
                            ...prevState,
                            page: nextValue,
                        }));
                    }}
                    hideColumns={hideColumns}
                    hideDetailFields={hideDetailFields}
                    ticketsLoading={isLoading}
                    liftOperators={liftOperators}
                    locale={locale}
                    translations={translations}
                />
            </Stack>
        </Fragment>
    );
};
